import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'

import { useAIChat as useOriginalAIChat } from 'hooks/websocket/useAIChat'
import { buildTranslate } from 'locales'
import type { SegmentSaveData } from 'slices/aiConversations/segmentVisualizationTypes'
import type { Message } from 'slices/aiConversations/types'

const t = buildTranslate('components.ai.websocket_connection')

interface AIChatConnectionContextValue {
  isConnected: boolean
  connectionError: string | null
  isLoading: boolean
  messages: Message[]
  currentStreamingMessage: Message | null
  awaitingResponse: boolean
  sendMessage: (content: string) => void
  saveSegment: (segmentData: SegmentSaveData) => void
  setIsLoading: (loading: boolean) => void
  setAwaitingResponse: (awaiting: boolean) => void
  addMessage: (message: Message) => void
  setMessages: (messages: Message[]) => void
  resetChatId: () => void
}

const AIChatConnectionContext =
  createContext<AIChatConnectionContextValue | null>(null)

interface AIChatConnectionProviderProps {
  children: React.ReactNode
}

/**
 * Provider that manages a single WebSocket connection for AI chat functionality.
 *
 * This provider ensures only one WebSocket connection is created and managed,
 * while allowing multiple components to safely access the chat data and functionality
 * through the useAIChat hook.
 */
export const AIChatConnectionProvider: React.FC<
  AIChatConnectionProviderProps
> = ({ children }) => {
  const chatData = useOriginalAIChat()
  const prevConnectedRef = useRef<boolean | null>(null)
  const connectionLostRef = useRef<boolean>(false)

  const handleMessageDropped = useCallback(() => {
    if (!connectionLostRef.current) {
      const userMessages = chatData.messages.filter(
        message => message.type === 'user'
      )
      if (userMessages.length > 0) {
        const latestUserSequence = Math.max(
          ...userMessages.map(message => message.sequenceNumber || 0)
        )
        chatData.addMessage({
          id: `connection-lost-${Date.now()}`,
          content: t('connection_lost_message'),
          type: 'ai',
          timestamp: new Date(),
          isMarkdown: false,
          sequenceNumber: latestUserSequence + 1, // Ensure it comes after user messages
          clientId: 'system-connection-status',
          isOptimistic: false,
        })
      }
      connectionLostRef.current = true
    }
  }, [chatData.messages, chatData.addMessage])

  useEffect(() => {
    if (chatData.setMessageDroppedCallback) {
      chatData.setMessageDroppedCallback(handleMessageDropped)
    }

    return () => {
      if (chatData.setMessageDroppedCallback) {
        chatData.setMessageDroppedCallback(null)
      }
    }
  }, [chatData.setMessageDroppedCallback, handleMessageDropped])

  useEffect(() => {
    const wasConnected = prevConnectedRef.current
    const isConnected = chatData.isConnected

    // Skip initial render
    if (wasConnected === null) {
      prevConnectedRef.current = isConnected
      return
    }

    if (!wasConnected && isConnected && connectionLostRef.current) {
      const userMessages = chatData.messages.filter(
        message => message.type === 'user'
      )
      if (userMessages.length > 0) {
        const latestUserSequence = Math.max(
          ...userMessages.map(message => message.sequenceNumber || 0)
        )
        chatData.addMessage({
          id: `connection-restored-${Date.now()}`,
          content: t('connection_restored_message'),
          type: 'ai',
          timestamp: new Date(),
          isMarkdown: false,
          sequenceNumber: latestUserSequence + 1, // Ensure it comes after user messages
          clientId: 'system-connection-status',
          isOptimistic: false,
        })
      }
      connectionLostRef.current = false
    }

    prevConnectedRef.current = isConnected
  }, [chatData.isConnected, chatData.messages, chatData.addMessage])

  return (
    <AIChatConnectionContext.Provider value={chatData}>
      {children}
    </AIChatConnectionContext.Provider>
  )
}

/**
 * Hook to access AI chat functionality.
 *
 * This hook provides access to WebSocket data and actions without managing
 * the connection itself. The connection is managed by AIChatConnectionProvider.
 *
 * Safe to call from multiple components - will not create multiple connections.
 */
export const useAIChat = () => {
  const context = useContext(AIChatConnectionContext)

  if (!context) {
    throw new Error('useAIChat must be used within AIChatConnectionProvider')
  }

  return context
}
